import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Star, TrendingUp, TrendingDown, 
  Filter, ChevronLeft, ChevronRight,
  Bell, Clock
} from 'lucide-react';
import { useMarketData } from '../hooks/useMarketData';
import { Skeleton } from '../components/ui/skeleton';
import { 
  AreaChart, Area, ResponsiveContainer 
} from 'recharts';

interface Market {
  id: string;
  symbol: string;
  name: string;
  price: number;
  change24h: number;
  changeValue: number;
  volume24h: number;
  marketCap: number;
  high24h: number;
  low24h: number;
  sparkline: number[];
  isFavorite: boolean;
  category: 'crypto' | 'african' | 'stable';
}

const MarketsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'price' | 'change' | 'volume' | 'marketCap'>('marketCap');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [category, setCategory] = useState<'all' | 'crypto' | 'african' | 'stable'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 20;

  const { isLoading } = useMarketData();

  // Generate comprehensive market data
  const markets: Market[] = useMemo(() => {
    const baseMarkets: Market[] = [
      { id: '1', symbol: 'BTC', name: 'Bitcoin', price: 96750.45, change24h: 2.34, changeValue: 2213.50, volume24h: 28500000000, marketCap: 1905000000000, high24h: 98200.00, low24h: 94500.00, sparkline: [94500, 95200, 94800, 96100, 96750], isFavorite: false, category: 'crypto' },
      { id: '2', symbol: 'ETH', name: 'Ethereum', price: 3456.78, change24h: -1.23, changeValue: -43.00, volume24h: 15200000000, marketCap: 415000000000, high24h: 3520.00, low24h: 3380.00, sparkline: [3520, 3490, 3450, 3420, 3456], isFavorite: false, category: 'crypto' },
      { id: '3', symbol: 'USDT', name: 'Tether', price: 1.00, change24h: 0.01, changeValue: 0.00, volume24h: 52000000000, marketCap: 95000000000, high24h: 1.01, low24h: 0.99, sparkline: [1.00, 1.00, 1.00, 1.00, 1.00], isFavorite: false, category: 'stable' },
      { id: '4', symbol: 'BNB', name: 'BNB', price: 712.45, change24h: 3.45, changeValue: 23.75, volume24h: 1800000000, marketCap: 105000000000, high24h: 725.00, low24h: 688.00, sparkline: [688, 695, 705, 710, 712], isFavorite: false, category: 'crypto' },
      { id: '5', symbol: 'SOL', name: 'Solana', price: 198.56, change24h: 5.67, changeValue: 10.65, volume24h: 3200000000, marketCap: 89000000000, high24h: 205.00, low24h: 187.00, sparkline: [187, 192, 195, 197, 198], isFavorite: false, category: 'crypto' },
      { id: '6', symbol: 'XRP', name: 'XRP', price: 2.34, change24h: -2.45, changeValue: -0.06, volume24h: 2800000000, marketCap: 134000000000, high24h: 2.42, low24h: 2.28, sparkline: [2.42, 2.38, 2.35, 2.32, 2.34], isFavorite: false, category: 'crypto' },
      { id: '7', symbol: 'NGNT', name: 'Nigeria Token', price: 0.00065, change24h: 2.30, changeValue: 0.000015, volume24h: 450000000, marketCap: 650000000, high24h: 0.00067, low24h: 0.00063, sparkline: [0.00063, 0.00064, 0.00065, 0.00066, 0.00065], isFavorite: false, category: 'african' },
      { id: '8', symbol: 'GHST', name: 'Ghana Token', price: 0.0082, change24h: 1.80, changeValue: 0.000145, volume24h: 280000000, marketCap: 410000000, high24h: 0.0084, low24h: 0.0080, sparkline: [0.0080, 0.0081, 0.0082, 0.0083, 0.0082], isFavorite: false, category: 'african' },
      { id: '9', symbol: 'KENT', name: 'Kenya Token', price: 0.0071, change24h: -0.50, changeValue: -0.000036, volume24h: 320000000, marketCap: 530000000, high24h: 0.0072, low24h: 0.0070, sparkline: [0.0072, 0.0071, 0.0070, 0.0071, 0.0071], isFavorite: false, category: 'african' },
      { id: '10', symbol: 'ZART', name: 'South Africa Token', price: 0.055, change24h: 3.20, changeValue: 0.0017, volume24h: 180000000, marketCap: 290000000, high24h: 0.056, low24h: 0.053, sparkline: [0.053, 0.054, 0.055, 0.055, 0.055], isFavorite: false, category: 'african' },
      { id: '11', symbol: 'EGPT', name: 'Egypt Token', price: 0.020, change24h: 0.80, changeValue: 0.00016, volume24h: 120000000, marketCap: 200000000, high24h: 0.021, low24h: 0.019, sparkline: [0.019, 0.020, 0.020, 0.020, 0.020], isFavorite: false, category: 'african' },
      { id: '12', symbol: 'ADA', name: 'Cardano', price: 1.05, change24h: -1.50, changeValue: -0.016, volume24h: 450000000, marketCap: 37000000000, high24h: 1.08, low24h: 1.02, sparkline: [1.08, 1.06, 1.05, 1.04, 1.05], isFavorite: false, category: 'crypto' },
      { id: '13', symbol: 'AVAX', name: 'Avalanche', price: 38.45, change24h: 4.20, changeValue: 1.55, volume24h: 580000000, marketCap: 14500000000, high24h: 39.50, low24h: 36.80, sparkline: [36.8, 37.5, 38.0, 38.2, 38.45], isFavorite: false, category: 'crypto' },
      { id: '14', symbol: 'DOT', name: 'Polkadot', price: 6.78, change24h: -0.80, changeValue: -0.055, volume24h: 220000000, marketCap: 9500000000, high24h: 6.90, low24h: 6.65, sparkline: [6.90, 6.85, 6.80, 6.75, 6.78], isFavorite: false, category: 'crypto' },
      { id: '15', symbol: 'LINK', name: 'Chainlink', price: 19.85, change24h: 2.10, changeValue: 0.41, volume24h: 380000000, marketCap: 11800000000, high24h: 20.50, low24h: 19.20, sparkline: [19.2, 19.5, 19.7, 19.8, 19.85], isFavorite: false, category: 'crypto' },
      { id: '16', symbol: 'MATIC', name: 'Polygon', price: 0.45, change24h: -3.20, changeValue: -0.015, volume24h: 290000000, marketCap: 4200000000, high24h: 0.47, low24h: 0.44, sparkline: [0.47, 0.46, 0.45, 0.44, 0.45], isFavorite: false, category: 'crypto' },
      { id: '17', symbol: 'UNI', name: 'Uniswap', price: 9.12, change24h: 1.50, changeValue: 0.135, volume24h: 180000000, marketCap: 5500000000, high24h: 9.35, low24h: 8.90, sparkline: [8.9, 9.0, 9.1, 9.1, 9.12], isFavorite: false, category: 'crypto' },
      { id: '18', symbol: 'LTC', name: 'Litecoin', price: 72.35, change24h: 0.90, changeValue: 0.65, volume24h: 320000000, marketCap: 5400000000, high24h: 74.00, low24h: 71.00, sparkline: [71, 71.5, 72, 72.2, 72.35], isFavorite: false, category: 'crypto' },
      { id: '19', symbol: 'BCH', name: 'Bitcoin Cash', price: 445.20, change24h: -1.80, changeValue: -8.15, volume24h: 150000000, marketCap: 8800000000, high24h: 456.00, low24h: 438.00, sparkline: [456, 450, 445, 442, 445], isFavorite: false, category: 'crypto' },
      { id: '20', symbol: 'ETC', name: 'Ethereum Classic', price: 24.56, change24h: 2.40, changeValue: 0.58, volume24h: 95000000, marketCap: 3600000000, high24h: 25.20, low24h: 23.80, sparkline: [23.8, 24.2, 24.4, 24.5, 24.56], isFavorite: false, category: 'crypto' },
      { id: '21', symbol: 'USDC', name: 'USD Coin', price: 1.00, change24h: 0.00, changeValue: 0.00, volume24h: 4800000000, marketCap: 42000000000, high24h: 1.01, low24h: 0.99, sparkline: [1.00, 1.00, 1.00, 1.00, 1.00], isFavorite: false, category: 'stable' },
      { id: '22', symbol: 'DAI', name: 'Dai', price: 1.00, change24h: 0.02, changeValue: 0.00, volume24h: 120000000, marketCap: 5300000000, high24h: 1.01, low24h: 0.99, sparkline: [1.00, 1.00, 1.00, 1.00, 1.00], isFavorite: false, category: 'stable' },
      { id: '23', symbol: 'BUSD', name: 'Binance USD', price: 1.00, change24h: -0.01, changeValue: 0.00, volume24h: 85000000, marketCap: 1800000000, high24h: 1.01, low24h: 0.99, sparkline: [1.00, 1.00, 1.00, 1.00, 1.00], isFavorite: false, category: 'stable' },
      { id: '24', symbol: 'TUSD', name: 'TrueUSD', price: 1.00, change24h: 0.01, changeValue: 0.00, volume24h: 45000000, marketCap: 950000000, high24h: 1.01, low24h: 0.99, sparkline: [1.00, 1.00, 1.00, 1.00, 1.00], isFavorite: false, category: 'stable' },
      { id: '25', symbol: 'SEN', name: 'Senegal Token', price: 0.0035, change24h: 5.20, changeValue: 0.00017, volume24h: 85000000, marketCap: 120000000, high24h: 0.0037, low24h: 0.0033, sparkline: [0.0033, 0.0034, 0.0035, 0.0036, 0.0035], isFavorite: false, category: 'african' },
    ];
    return baseMarkets.map(m => ({ ...m, isFavorite: favorites.includes(m.id) }));
  }, [favorites]);

  // Filter and sort
  const filteredMarkets = useMemo(() => {
    let result = markets.filter(m => {
      const matchesSearch = m.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          m.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = category === 'all' || m.category === category;
      return matchesSearch && matchesCategory;
    });

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'price': comparison = a.price - b.price; break;
        case 'change': comparison = a.change24h - b.change24h; break;
        case 'volume': comparison = a.volume24h - b.volume24h; break;
        case 'marketCap': comparison = a.marketCap - b.marketCap; break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [markets, searchQuery, category, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredMarkets.length / itemsPerPage);
  const paginatedMarkets = filteredMarkets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleFavorite = (id: string) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const formatNumber = (num: number, decimals = 2) => {
    if (num >= 1e9) return `$${(num / 1e9).toFixed(decimals)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(decimals)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(decimals)}K`;
    return `$${num.toFixed(decimals)}`;
  };

  const formatPrice = (price: number) => {
    if (price < 0.01) return price.toFixed(6);
    if (price < 1) return price.toFixed(4);
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Markets</h1>
              <p className="text-muted-foreground">Track prices and explore trading opportunities</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">24h Volume: $89.2B</span>
              </div>
              <button className="p-2 bg-card rounded-lg text-muted-foreground hover:text-foreground transition-colors">
                <Bell className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Market Overview */}
      <div className="border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Market Cap', value: '$2.8T', change: '+2.4%', positive: true },
              { label: '24h Volume', value: '$89.2B', change: '+15.3%', positive: true },
              { label: 'BTC Dominance', value: '52.3%', change: '-0.5%', positive: false },
              { label: 'Active Markets', value: '2,456', change: '+12', positive: true },
            ].map((stat, i) => (
              <div key={i} className="nex-card p-4">
                <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-foreground">{stat.value}</span>
                  <span className={`text-xs ${stat.positive ? 'text-emerald-500' : 'text-red-500'}`}>
                    {stat.change}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or symbol..."
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              />
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'crypto', label: 'Crypto' },
                { key: 'african', label: 'African Tokens' },
                { key: 'stable', label: 'Stablecoins' },
              ].map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setCategory(cat.key as any)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    category === cat.key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Sort & Filter */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field as any);
                  setSortOrder(order as any);
                }}
                className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary"
              >
                <option value="marketCap-desc">Market Cap (High to Low)</option>
                <option value="marketCap-asc">Market Cap (Low to High)</option>
                <option value="price-desc">Price (High to Low)</option>
                <option value="price-asc">Price (Low to High)</option>
                <option value="change-desc">Change (High to Low)</option>
                <option value="change-asc">Change (Low to High)</option>
                <option value="volume-desc">Volume (High to Low)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Market Table */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <div className="nex-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-card">
              <tr>
                <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 w-10"></th>
                <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">#</th>
                <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">Name</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">Price</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">24h Change</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">24h High</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">24h Low</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">Volume (24h)</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">Market Cap</th>
                <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">Chart</th>
                <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">Trade</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array(10).fill(0).map((_, i) => (
                  <tr key={i} className="border-b border-border">
                    <td colSpan={11} className="py-4 px-4">
                      <Skeleton className="h-12" />
                    </td>
                  </tr>
                ))
              ) : (
                paginatedMarkets.map((market, index) => (
                  <tr key={market.id} className="border-b border-border hover:bg-card transition-colors">
                    <td className="py-4 px-4">
                      <button
                        onClick={() => toggleFavorite(market.id)}
                        className={`transition-colors ${market.isFavorite ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                      >
                        <Star className={`w-4 h-4 ${market.isFavorite ? 'fill-[#F0B90B]' : ''}`} />
                      </button>
                    </td>
                    <td className="py-4 px-4 text-muted-foreground text-sm">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center text-black font-bold text-xs">
                          {market.symbol[0]}
                        </div>
                        <div>
                          <p className="text-foreground font-medium">{market.name}</p>
                          <p className="text-xs text-muted-foreground">{market.symbol}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className="text-foreground font-mono">${formatPrice(market.price)}</span>
                    </td>
                    <td className="text-right py-4 px-4">
                      <div className={`flex items-center justify-end gap-1 ${market.change24h >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {market.change24h >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        <span className="font-mono">{market.change24h >= 0 ? '+' : ''}{market.change24h.toFixed(2)}%</span>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4 text-muted-foreground font-mono text-sm">
                      ${formatPrice(market.high24h)}
                    </td>
                    <td className="text-right py-4 px-4 text-muted-foreground font-mono text-sm">
                      ${formatPrice(market.low24h)}
                    </td>
                    <td className="text-right py-4 px-4 text-muted-foreground font-mono text-sm">
                      {formatNumber(market.volume24h)}
                    </td>
                    <td className="text-right py-4 px-4 text-muted-foreground font-mono text-sm">
                      {formatNumber(market.marketCap)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="w-24 h-10 mx-auto">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={market.sparkline.map((v, i) => ({ value: v, index: i }))}>
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={market.change24h >= 0 ? '#0ECB81' : '#F6465D'}
                              fill={market.change24h >= 0 ? '#0ECB81' : '#F6465D'}
                              fillOpacity={0.2}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <Link
                        to={`/spot?pair=${market.symbol}_USDT`}
                        className="px-4 py-2 bg-[#F0B90B] text-black rounded-lg text-sm font-medium hover:brightness-110 transition-all"
                      >
                        Trade
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredMarkets.length)} of {filteredMarkets.length} markets
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      currentPage === page 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-card text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketsPage;
